import React, { useState, useEffect, useRef } from 'react'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api, { API_BASE, getImageUrl } from '../../utils/api'
import { toast } from 'react-toastify'

export default function AdminProfile() {
    const { user, token, updateUser } = useAuth()
    const [saving, setSaving] = useState(false)
    const [changingPwd, setChangingPwd] = useState(false)
    const [uploading, setUploading] = useState(false)
    const avatarInputRef = useRef<HTMLInputElement>(null)
    
    const [form, setForm] = useState({
        name: '', email: '', phone: '', title: '', address: '', city: '', state: '', zip: '', about: '',
        facebook: '', twitter: '', googleplus: '', linkedin: '', avatar: ''
    })

    const [pwdForm, setPwdForm] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    })

    useEffect(() => {
        if (user) setForm({
            name: user.name || '',
            email: user.email || '',
            phone: user.phone || '',
            title: user.title || '',
            address: user.address || '',
            city: user.city || '',
            state: user.state || '',
            zip: user.zip || '',
            about: user.about || '',
            facebook: user.facebook || '',
            twitter: user.twitter || '',
            googleplus: user.googleplus || '',
            linkedin: user.linkedin || '',
            avatar: user.avatar || ''
        })
    }, [user])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setForm(p => ({ ...p, [e.target.name]: e.target.value }))
    }

    const handlePwdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPwdForm(p => ({ ...p, [e.target.name]: e.target.value }))
    }

    const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file || !token) return
        const uploadForm = new FormData()
        uploadForm.append('images', file)
        setUploading(true)
        try {
            const res = await fetch(`${API_BASE}/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: uploadForm
            })
            const data = await res.json()
            if (res.ok && data.urls && data.urls.length > 0) {
                setForm(p => ({ ...p, avatar: data.urls[0] }))
                toast.success('Profile picture updated. Click save to apply changes.')
            } else {
                toast.error(data.message || 'Upload failed')
            }
        } catch {
            toast.error('Network error during upload')
        } finally {
            setUploading(false)
        }
    }

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) return
        setSaving(true)
        try {
            const data = await api.updateMe(token, form)
            if (data._id) { 
                updateUser(data)
                toast.success('Profile updated successfully')
            } else {
                toast.error(data.message || 'Update failed')
            }
        } catch (err) {
            toast.error('Server error')
        } finally {
            setSaving(false)
        }
    }

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!token) return
        if (pwdForm.newPassword !== pwdForm.confirmPassword) {
            toast.error('Security override: Passwords do not match')
            return
        }
        setChangingPwd(true)
        try {
            const res = await api.changePassword(token, {
                oldPassword: pwdForm.oldPassword,
                newPassword: pwdForm.newPassword
            })
            if (res.message === 'Password changed successfully') {
                toast.success('Password updated successfully')
                setPwdForm({ oldPassword: '', newPassword: '', confirmPassword: '' })
            } else {
                toast.error(res.message || 'Password update failed')
            }
        } catch (err) {
            toast.error('Server error')
        } finally {
            setChangingPwd(false)
        }
    }

    return (
        <AdminLayout>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                <div>
                     <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Profile Settings</h1>
                    <p className="text-slate-500 font-medium text-base">Update your personal information and account security.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
                {/* Left Column - Main Details */}
                <div className="lg:col-span-8 space-y-8">
                    {/* Personal Info Card */}
                    <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-xl overflow-hidden relative">
                         <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-none blur-[80px] -mr-32 -mt-32 pointer-events-none"></div>
                         
                         <div className="relative z-10">
                            <div className="p-8 border-b border-slate-100 dark:border-slate-800 bg-white/30">
                                 <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Personal Information</h3>
                                <p className="text-base font-medium text-slate-500 mt-1">Manage your basic profile details and contact information.</p>
                            </div>

                            <form onSubmit={handleSave} className="p-8 space-y-8">
                                <div className="flex flex-col md:flex-row gap-8 items-start">
                                    <div className="relative group shrink-0">
                                        <div 
                                            onClick={() => avatarInputRef.current?.click()}
                                            className="w-32 h-32 rounded-md bg-blue-50 dark:bg-slate-800 border-2 border-dashed border-blue-900/10 dark:border-slate-700 overflow-hidden cursor-pointer group-hover:border-blue-950 transition-all flex items-center justify-center relative"
                                        >
                                            {form.avatar ? (
                                                <img src={getImageUrl(form.avatar)} alt="Profile" className="w-full h-full object-cover transition-transform group-hover:scale-110 duration-700" />
                                            ) : (
                                                <i className="fa-solid fa-user-tie text-4xl text-blue-950/10"></i>
                                            )}
                                            <div className="absolute inset-0 bg-[#05144b]/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                <i className="fa-solid fa-camera text-white text-xl"></i>
                                            </div>
                                            {uploading && (
                                                <div className="absolute inset-0 bg-white/80 dark:bg-slate-900/80 flex items-center justify-center">
                                                    <div className="w-6 h-6 border-2 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>
                                                </div>
                                            )}
                                        </div>
                                        <input type="file" ref={avatarInputRef} className="hidden" accept="image/*" onChange={handleAvatarUpload} />
                                    </div>
                                    <div className="flex-1 space-y-2">
                                         <h4 className="text-3xl font-bold text-slate-900 dark:text-white">{form.name || 'Admin User'}</h4>
                                        <p className="text-lg font-semibold text-slate-500">{form.title || 'System Administrator'}</p>
                                         <div className="flex gap-2 pt-2">
                                            <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-lg text-xs font-bold border border-blue-100">Role: Admin</span>
                                            <span className="px-3 py-1 bg-emerald-50 text-emerald-600 rounded-lg text-xs font-bold border border-emerald-100">Account Verified</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                                     <div className="space-y-2">
                                         <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Full Name</label>
                                        <input 
                                            type="text" name="name" value={form.name} onChange={handleChange}
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                            placeholder="Enter your name"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Email Address</label>
                                        <input 
                                            type="email" name="email" value={form.email} onChange={handleChange}
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                            placeholder="admin@example.com"
                                        />
                                    </div>
                                     <div className="space-y-2">
                                        <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Job Title</label>
                                        <input 
                                            type="text" name="title" value={form.title} onChange={handleChange}
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                            placeholder="e.g. Administrator"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Phone Number</label>
                                        <input 
                                            type="text" name="phone" value={form.phone} onChange={handleChange}
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                            placeholder="+91 XXXXX XXXXX"
                                        />
                                    </div>
                                     <div className="md:col-span-2 space-y-2">
                                        <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Office Address</label>
                                        <input 
                                            type="text" name="address" value={form.address} onChange={handleChange}
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                            placeholder="Enter your address"
                                        />
                                    </div>
                                     <div className="grid grid-cols-3 md:col-span-2 gap-4">
                                        <div className="space-y-2">
                                            <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">City</label>
                                            <input 
                                                type="text" name="city" value={form.city} onChange={handleChange}
                                                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">State</label>
                                            <input 
                                                type="text" name="state" value={form.state} onChange={handleChange}
                                                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Zip Code</label>
                                            <input 
                                                type="text" name="zip" value={form.zip} onChange={handleChange}
                                                className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                            />
                                        </div>
                                    </div>
                                     <div className="md:col-span-2 space-y-2">
                                         <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">About Me</label>
                                        <textarea 
                                            name="about" value={form.about} onChange={handleChange}
                                            rows={4}
                                            className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm resize-none"
                                            placeholder="Write a brief description about yourself..."
                                        />
                                    </div>
                                </div>

                                <div className="pt-6">
                                     <button 
                                        type="submit" 
                                        disabled={saving}
                                        className="w-full md:w-auto px-10 py-3.5 bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-50 rounded-xl text-sm font-bold shadow-md transition-all disabled:opacity-50 active:scale-95"
                                    >
                                        {saving ? 'Saving...' : 'Update Profile'}
                                    </button>
                                </div>
                            </form>
                         </div>
                    </div>
                </div>

                {/* Right Column - Security & Social */}
                <div className="lg:col-span-4 space-y-8">
                    {/* Security Card */}
                    <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-xl overflow-hidden relative">
                         <div className="absolute top-0 right-0 w-48 h-48 bg-rose-50/30 rounded-none blur-[60px] -mr-24 -mt-24 pointer-events-none"></div>
                         
                         <div className="relative z-10">
                            <div className="p-8 border-b border-slate-100 dark:border-slate-800 bg-white/30">
                                 <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Security Settings</h3>
                                <p className="text-base font-medium text-slate-500 mt-1">Update your password and security credentials.</p>
                            </div>

                            <form onSubmit={handleUpdatePassword} className="p-8 space-y-6">
                                 <div className="space-y-2">
                                    <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Current Password</label>
                                    <input 
                                        type="password" name="oldPassword" value={pwdForm.oldPassword} onChange={handlePwdChange}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-rose-500 transition-all shadow-sm"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">New Password</label>
                                    <input 
                                        type="password" name="newPassword" value={pwdForm.newPassword} onChange={handlePwdChange}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-base font-bold text-slate-700 dark:text-slate-300 mb-2 ml-1">Confirm Password</label>
                                    <input 
                                        type="password" name="confirmPassword" value={pwdForm.confirmPassword} onChange={handlePwdChange}
                                        className="w-full bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-4 text-lg font-medium text-slate-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all shadow-sm"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                                 <button 
                                    type="submit" 
                                    disabled={changingPwd}
                                    className="w-full py-3.5 bg-white border-2 border-rose-600 text-rose-600 hover:bg-rose-50 rounded-xl text-sm font-bold shadow-md transition-all disabled:opacity-50 active:scale-95"
                                >
                                    {changingPwd ? 'Updating...' : 'Change Password'}
                                </button>
                            </form>
                         </div>
                    </div>

                    {/* Social Card */}
                    <div className="bg-white dark:bg-slate-900 rounded-md border border-blue-900/10 dark:border-slate-800 shadow-xl overflow-hidden relative">
                         <div className="relative z-10">
                            <div className="p-8 border-b border-slate-100 dark:border-slate-800 bg-white/30">
                                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Social Media Links</h3>
                                <p className="text-base font-medium text-slate-500 mt-1">Connect your social accounts to your profile.</p>
                            </div>

                            <div className="p-8 space-y-4">
                                <div className="flex items-center gap-4 group">
                                    <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-all">
                                        <i className="fa-brands fa-facebook-f text-xl"></i>
                                    </div>
                                    <input 
                                        type="text" name="facebook" value={form.facebook} onChange={handleChange}
                                        className="flex-1 bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-3 text-lg font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-blue-500 shadow-sm"
                                        placeholder="Facebook Profile"
                                    />
                                </div>
                                <div className="flex items-center gap-4 group">
                                    <div className="w-12 h-12 rounded-xl bg-sky-50 text-sky-600 flex items-center justify-center shadow-sm group-hover:bg-sky-600 group-hover:text-white transition-all">
                                        <i className="fa-brands fa-twitter text-xl"></i>
                                    </div>
                                    <input 
                                        type="text" name="twitter" value={form.twitter} onChange={handleChange}
                                        className="flex-1 bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-3 text-lg font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-blue-500 shadow-sm"
                                        placeholder="Twitter Handle"
                                    />
                                </div>
                                <div className="flex items-center gap-4 group">
                                    <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-800 flex items-center justify-center shadow-sm group-hover:bg-blue-800 group-hover:text-white transition-all">
                                        <i className="fa-brands fa-linkedin-in text-xl"></i>
                                    </div>
                                    <input 
                                        type="text" name="linkedin" value={form.linkedin} onChange={handleChange}
                                        className="flex-1 bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 border rounded-xl px-5 py-3 text-lg font-medium text-slate-900 dark:text-white focus:ring-1 focus:ring-blue-500 shadow-sm"
                                        placeholder="LinkedIn Profile"
                                    />
                                </div>
                            </div>
                         </div>
                    </div>
                </div>
            </div>
        </AdminLayout>
    )
}


